import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { OrganizationContext } from '@stockpilot/shared';

const ORGANIZATION_KEY = 'sp.organizationId';
const BRANCH_KEY = 'sp.branchId';
const THEME_KEY = 'sp.theme';

export type ThemePreference = 'light' | 'dark' | 'system';

export interface TenantState {
  organizationId: string | null;
  branchId: string | null;
  context: OrganizationContext | null;
  theme: ThemePreference;
  sidebarCollapsed: boolean;
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* storage unavailable — selection simply will not persist */
  }
}

const storedTheme = read(THEME_KEY);

const initialState: TenantState = {
  organizationId: read(ORGANIZATION_KEY),
  branchId: read(BRANCH_KEY),
  context: null,
  theme:
    storedTheme === 'light' || storedTheme === 'dark' || storedTheme === 'system'
      ? storedTheme
      : 'system',
  sidebarCollapsed: false,
};

const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    organizationSelected(state, action: PayloadAction<string | null>) {
      if (state.organizationId !== action.payload) {
        // Switching business invalidates the branch and the cached context.
        state.branchId = null;
        state.context = null;
        write(BRANCH_KEY, null);
      }
      state.organizationId = action.payload;
      write(ORGANIZATION_KEY, action.payload);
    },
    branchSelected(state, action: PayloadAction<string | null>) {
      state.branchId = action.payload;
      write(BRANCH_KEY, action.payload);
    },
    contextLoaded(state, action: PayloadAction<OrganizationContext>) {
      state.context = action.payload;
      // Fall back to the default branch the first time a business is opened.
      if (!state.branchId) {
        const fallback =
          action.payload.branches.find((branch) => branch.isDefault) ??
          action.payload.branches[0];
        if (fallback) {
          state.branchId = fallback.id;
          write(BRANCH_KEY, fallback.id);
        }
      }
    },
    themeChanged(state, action: PayloadAction<ThemePreference>) {
      state.theme = action.payload;
      write(THEME_KEY, action.payload);
    },
    sidebarToggled(state, action: PayloadAction<boolean | undefined>) {
      state.sidebarCollapsed = action.payload ?? !state.sidebarCollapsed;
    },
    tenantCleared(state) {
      state.organizationId = null;
      state.branchId = null;
      state.context = null;
      write(ORGANIZATION_KEY, null);
      write(BRANCH_KEY, null);
    },
  },
});

export const {
  organizationSelected,
  branchSelected,
  contextLoaded,
  themeChanged,
  sidebarToggled,
  tenantCleared,
} = tenantSlice.actions;

export default tenantSlice.reducer;
